import { AxiosError, AxiosResponse } from 'axios';

import { Component, Prop, Vue } from 'vue-property-decorator';
import RequestKanbanBoardBlock from '@/components/request/RequestKanbanBoardBlock/RequestKanbanBoardBlock.vue';
import RequestSearchInput from '@/components/request/RequestSearchInput/RequestSearchInput.vue';
import RequestStylistFilter from '@/components/request/RequestStylistFilter/RequestStylistFilter.vue';
import RequestTypeFilter from '@/components/request/RequestTypeFilter/RequestTypeFilter.vue';
import { BoxRequest, Statuses as BoxRequestStatuses, StatusKey as BoxRequestStatusKey, ListFilters as BoxRequestListFilters, ListInclude as BoxRequestListInclude } from '@/models/BoxRequest';
import { User } from '@/models/User';
import ErrorHandler from '@/support/ErrorHandler';
import LoaderCard from '@/components/loader-card/LoaderCard.vue';
import dragula, { Drake } from 'dragula';
import { isNumber, uniqBy } from 'lodash';
import { cloneDeep } from 'lodash';

@Component<RequestKanbanBoard>({
  components: {
    'request-kanban-board-block': RequestKanbanBoardBlock,
    'request-search-input': RequestSearchInput,
    'request-stylist-filter': RequestStylistFilter,
    'request-type-filter': RequestTypeFilter,
    LoaderCard,
  }
})
export default class RequestKanbanBoard extends Vue {
  @Prop()
  protected user!: User;

  @Prop({default: true})
  protected showFilter!: boolean;

  protected requestSearchInput: string = '';

  protected requestStylistFilter: string = '';

  protected requestTypeFilter: string = '';

  protected drake: Drake | null = null;

  protected requests: Record<string,BoxRequest[]> = {};

  protected allRequests: BoxRequest[] = [];

  protected allRequestsInVue: BoxRequest[] = [];

  protected requestsLoading: string[] = [];

  protected isLoading = true;

  protected totalsSet = false;

  protected kanbanCounts = [];

  protected stylists: User[] = [];

  protected stylistsLoaded = false;

  protected stageTotals: Record<string, number> = {}

  protected requestPagePerStage: Record<string, number> = {};

  protected lastPagePerStage: Record<string, number> = {};

  protected lastScrollPositionPerStage: Record<string, number> = {};

  protected sortOptions: sortObject[] = []

  protected mounted(): void {
    this.$nextTick(() => {
      for (const [key, value] of Object.entries(this.kanbanStages)) {
        this.sortOptions.push({ name: '', value: 'DESC', stage: key})
      };
      this.initialize();
    });
  }

  protected async initialize(): Promise<void> {
    Object.keys(this.kanbanStages).forEach((stage) => {
      this.requestPagePerStage[stage] = 0;
      this.lastScrollPositionPerStage[stage] = 0;
      this.lastPagePerStage[stage] = 0;
    });

    await this.fetchStylists();
    await this.fetchRequests();

    this.$nextTick(() => {
      this.initializeDragula();
    });
  }

  protected initializeDragula(): void {
    const kanbanColumns = document.getElementsByClassName('kanban-column');
    if (!kanbanColumns) { return; }

    this.drake = dragula([...kanbanColumns], {
      revertOnSpill: true,
      invalid: (element?: Element) => {
        if (!element) { return true; }
        return element.classList.contains('kanban-column-header');
      },

    });

    this.drake.on('drop', (element: Element, target: Element, source: Element) => {
      const sourceStatus = source.getAttribute('data-status') || '';
      const targetStatus = target.getAttribute('data-status') || '';
      const requestId = element.getAttribute('data-request') || '';
      this.handleKanbanBlockUpdate(requestId, targetStatus, sourceStatus);
    });

    this.isLoading = false;
  }

  protected async toggleSortStage(stage: string, property: string): Promise<void> {
    let sortOption = this.sortOptions.find(sortOption => sortOption.stage == stage);
    if(! sortOption) {
      return;
    }

    sortOption.name = property;
    sortOption.value = sortOption.value === 'DESC' ? 'ASC' : 'DESC';

    this.requestPagePerStage[stage] = 0;
    this.lastScrollPositionPerStage[stage] = 0;

    this.allRequests = this.allRequests.filter(request => request.status !== stage);

    this.fetchRequestsForStatus(stage)
  }

  protected resetRequestPages(): void {
    Object.keys(this.requestPagePerStage).forEach(key => this.requestPagePerStage[key] = 0);
  }

  protected sortIsActive(stage: string): boolean {
    const option = this.sortOptions.find(sortOption => sortOption.stage == stage);

    if (!option) {
      return false;
    }

    return option.value === 'ASC';
  }

  public async refresh(): Promise<void> {
    this.resetRequestPages();
    await this.fetchRequests();
  }

  protected handleStageRefresh(status: string) {
    if(status === 'pending') { this.fetchRequestsForStatus('on_hold_photos', true) }
    this.fetchRequestsForStatus(status, true)

    // Reset the last scroll position per stage
    this.lastScrollPositionPerStage = {};
  }

  protected async handleKanbanBlockUpdate(id: string, newStatus: string, lastStatus: string): Promise<void> {
    await this.updateRequestStatus(id, newStatus, lastStatus);
  }

  protected async fetchStylists() : Promise<void> {
    const users: User[] = await new User()
      .filter({
        role: 'stylist',
        is_active: true,
      })
      .sort('name')
      .limit(999)
      .all()
      .catch((error: AxiosError): User[] => {
        ErrorHandler.network(error);
        return [];
      });
    this.stylists = users;
    this.stylistsLoaded = true;
  }

  protected async fetchRequests(): Promise<void> {
    this.allRequests = [];
    const requestPromises = Object.keys(this.kanbanStages).map((stage) => this.fetchRequestsForStatus(stage) );
    await Promise.all(requestPromises);
    this.redrawRequests();
  }

  protected async fetchRequestsForStatus(status: string, refreshCurrentPage= false): Promise<void> {

    if (this.requestsLoading.includes(status)) { return; }
    this.requestsLoading.push(status);

    const limit = 10;

    // Which page needs fetching
    let page = 0;
    if (this.requestPagePerStage[status] && isNumber(this.requestPagePerStage[status])) {
      page = this.requestPagePerStage[status]
    } else {
      this.requestPagePerStage[status] = 0
    }

    if (!refreshCurrentPage) { page += 1;}

    // When already on the last page remove loading & return
    if (this.lastPagePerStage[status] > 0 && this.requestPagePerStage[status] >= this.lastPagePerStage[status] && !refreshCurrentPage) {
      this.requestsLoading = this.requestsLoading.filter(currentStatus => currentStatus !== status);
      return;
    }

    const sortOption = this.sortOptions.find(sortOption => sortOption.stage == status);

    const requests: BoxRequest[] = await new BoxRequest()
      .include(this.includes)
      .filter(this.filters)
      .filter({status,})
      .sort(sortOption && sortOption.name ? sortOption.name : 'created_at', sortOption && sortOption.value ? sortOption.value : 'DESC')
      .page(page)
      .limit(limit)
      .all()
      .catch((error: AxiosError): BoxRequest[] => {
        ErrorHandler.network(error);
        return [];
      });

    // If im not refreshing the current page in need increment for the next API call
    if (!refreshCurrentPage) { this.requestPagePerStage[status] += 1;}

    // If I have results set the last_page
    if (requests[0] && requests[0].meta) {
      if ((requests[0].meta as any).last_page) {
        this.lastPagePerStage[status] = (requests[0].meta as any).last_page;
      }
      this.stageTotals[status] = (requests[0].meta as Record<string, number>)[status] || 0;
    } else {
      this.stageTotals[status] = 0;
    }

    if (refreshCurrentPage) {
      const otherStatusRequests = this.allRequests.filter((request) => request.status !== status);
      this.allRequests = uniqBy([...otherStatusRequests, ...requests], 'id')
    } else {
      this.allRequests = uniqBy([...this.allRequests, ...requests], 'id');
    }

    this.requestsLoading = this.requestsLoading.filter(currentStatus => currentStatus !== status);
    this.redrawRequests();
  }

  protected async updateRequestStatus(id: string, newStatus: string, lastStatus: string): Promise<void> {
    if (! id || ! newStatus) {return; }

    await new BoxRequest({id: id})
      .update({status: newStatus})
      .then((updatedRequest: BoxRequest) => {
        //Update the 'status_updated_at' timestamp
        let updateItem = this.allRequests.find((request) => request.id === id);
        if (!updateItem) {
          return;
        }

        // Note: do not redraw, because then the just dragged item's position will change
        let index = this.allRequests.indexOf(updateItem);
        this.allRequests[index].status_updated_at = updatedRequest.status_updated_at;
        this.allRequests[index].status = updatedRequest.status;
      })
      .catch(() => {
        Promise.all([
        this.fetchRequestsForStatus(newStatus, true),
        this.fetchRequestsForStatus(lastStatus, true)
      ]);
    });

    const totals = {...this.stageTotals};
    totals[lastStatus] += -1;
    totals[newStatus] += 1;
    this.$set(this, 'stageTotals', totals);
  }

  protected handleStylistFilterChange(value: any): void {
    this.requestStylistFilter = value;
    this.refresh();
  }

  protected handleRequestSearchInput(value: string): void {
    this.requestSearchInput = value;
    this.refresh();
  }

  protected handleColumnScroll(event: Event, kanbanStage: string): void {
    const element = event.target as HTMLDivElement;

    if (!this.lastScrollPositionPerStage[kanbanStage]) {
      this.lastScrollPositionPerStage[kanbanStage] = 0;
    }

    if (element.scrollTop >= (element.scrollHeight - element.offsetHeight - 16) && this.lastScrollPositionPerStage[kanbanStage] < element.scrollTop) {
      this.lastScrollPositionPerStage[kanbanStage] = element.scrollTop;
      this.fetchRequestsForStatus(kanbanStage);
    }
  }

  protected get includes(): BoxRequestListInclude[] {
    return ['orders', 'stylist', 'user', 'meta'];
  }

  protected get filters(): BoxRequestListFilters {
    return {
      ...this.requestSearchInput ? { search: this.requestSearchInput } : {},
      ...this.requestStylistFilter ? { stylist: this.requestStylistFilter } : {},
      ...this.requestTypeFilter ? { type: this.requestTypeFilter } : {},
    };
  }

  protected get kanbanStages(): BoxRequestStatuses {
    return BoxRequest.statuses;
  }

  protected get isPlanner() {
    if (! this.$store || !this.$store.state || ! this.$store.state.Auth) {
      return false;
    }

    // @ts-ignore
    return this.$store.state.Auth.isPlanner()
  }

  protected redrawRequests()
  {
    // This is a bit of a hack
    // Because we don't know the position we dragged to
    // And we don't want to redraw after dragging, because then we lose the position
    // We will redraw the requests on sorting or fetching more etc
    this.allRequestsInVue = cloneDeep(this.allRequests);
  }
}

type sortObject = {
  name?: string,
  value?: any,
  stage?: string,
}
